import React, { useEffect, useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    List,
    ListItemButton,
    ListItemAvatar,
    ListItemText,
    Avatar,
    CircularProgress,
    Typography,
} from '@mui/material';
import { useDebounce } from 'use-debounce';
import { adminSearchUsers } from '../../../admin.users.api';
import { UserData } from '../../../../../utils';

interface UserSearchDialogProps {
    open: boolean;
    onClose: () => void;
    onSelect: (user: UserData) => void;
}

const UserSearchDialog: React.FC<UserSearchDialogProps> = ({ open, onClose, onSelect }) => {
    const [searchValue, setSearchValue] = useState('');
    const [debouncedSearchValue] = useDebounce(searchValue, 300);
    const [results, setResults] = useState<UserData[]>([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) {
            return;
        }
        if (debouncedSearchValue.trim().length < 2) {
            setResults([]);
            return;
        }
        setLoading(true);
        adminSearchUsers(debouncedSearchValue.trim())
            .then((response) => setResults(response.data.data))
            .catch(() => setResults([]))
            .finally(() => setLoading(false));
    }, [debouncedSearchValue, open]);

    const handleClose = () => {
        setSearchValue('');
        setResults([]);
        onClose();
    };

    const handleSelect = (user: UserData) => {
        setSearchValue('');
        setResults([]);
        onSelect(user);
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>Search for a User</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Search by name, username, email, phone number, or UUID.
                </Typography>
                <TextField
                    autoFocus
                    fullWidth
                    label="Search"
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                    placeholder="e.g. Jane Doe, jane@email.com, +15551234567"
                    size="small"
                    InputProps={{
                        endAdornment: loading ? <CircularProgress size={20} /> : null,
                    }}
                />
                <List sx={{ mt: 1, maxHeight: 400, overflow: 'auto' }}>
                    {results.map((user) => (
                        <ListItemButton key={user.uuid} onClick={() => handleSelect(user)}>
                            <ListItemAvatar>
                                <Avatar src={user.profile_photo ?? ''} />
                            </ListItemAvatar>
                            <ListItemText
                                primary={`${user.first_name} ${user.last_name}`}
                                secondary={`@${user.user_name} · ${user.email}${
                                    user.phone ? ` · ${user.phone}` : ''
                                }`}
                            />
                        </ListItemButton>
                    ))}
                    {!loading &&
                        debouncedSearchValue.trim().length >= 2 &&
                        results.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                                No users found.
                            </Typography>
                        )}
                </List>
            </DialogContent>
        </Dialog>
    );
};

export default UserSearchDialog;
